/// <amd-module name="Comments/Editor/Components/CommentEditor" />
import * as CommonResources from "Comments/Common/Resources";
import { ICommentEditorProps, IRenderedEditor, IRenderEditorProps } from "Comments/Editor/Components/CommentEditor.Props";
import * as Resources from "Comments/Editor/Resources";
import { IMarkdownComponent, IMarkdownComponentProps } from "MarkdownEditor/Components/MarkdownComponent.Props";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Dialog } from "VSSUI/Dialog";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";

const MarkdownComponentDependencies = ["ms.vss-markdown.markdown-editor"];
const ValidAttachmentTypes = ["image/*"];
const FileTypeToExtension: { [type: string]: string } = {
    "image/jpg": "jpg",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/bmp": "bmp",
    "image/gif": "gif",
    "image/tif": "tif",
    "image/tiff": "tif"
};

export interface ICommentEditorState {
    isDirty: boolean;
    value: string;
}

/**
 * Editor for comment.
 */
export class CommentEditor extends React.PureComponent<ICommentEditorProps, ICommentEditorState> {
    public static defaultProps: Partial<ICommentEditorProps> = {
        uploadImageSizeLimitByte: 26214400 // 25 * 1024 * 1024 => 25MB
    };

    private _originalValue: string;
    private _showDiscardDialog = new ObservableValue<boolean>(false);
    private _editor: IRenderedEditor | IMarkdownComponent | undefined | null;
    private _isDisposing: boolean;
    private _blobUrlToFinalUrlMap: { [blobUrl: string]: string } | null = {};

    constructor(props: ICommentEditorProps) {
        super(props);

        this._originalValue = props.originalText != undefined ? props.originalText : props.comment.text;
        this.state = { isDirty: this._getDirtyState(props.comment.text), value: props.comment.text };
    }

    public render(): JSX.Element {
        const { className, onRenderEditor = this._onRenderDefaultEditor, comment, uploadImageHandler, ariaLabel, ariaLabelledBy, placeholder } = this.props;
        return (
            <div className={css("comment-editor", className)}>
                {onRenderEditor({ ref: this._editorRef, onChange: this._onChange, comment, uploadImageHandler, ariaLabel, ariaLabelledBy, placeholder })}
                {this._renderButtons()}
                {this._renderDiscardDraftDialog()}
            </div>
        );
    }

    public componentDidUpdate(prevProps: ICommentEditorProps, prevState: ICommentEditorState): void {
        const { isDirty } = this.state;
        const { comment, onDirtyChange } = this.props;

        if (onDirtyChange && prevState.isDirty !== isDirty) {
            onDirtyChange(comment.id, isDirty);
        }
    }

    public componentWillUnmount(): void {
        const { comment, onDirtyChange } = this.props;
        this._isDisposing = true;
        if (onDirtyChange && this.state.isDirty) {
            onDirtyChange(comment.id, false);
        }
        if (this._blobUrlToFinalUrlMap) {
            Object.keys(this._blobUrlToFinalUrlMap).forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
            this._blobUrlToFinalUrlMap = null;
        }
    }

    private _onRenderDefaultEditor = (props: IRenderEditorProps): JSX.Element => {
        const { onRenderPreview, uploadImageHandler, ariaLabel, ariaLabelledBy, placeholder } = this.props;

        return (
            <>
                <WrappedComponent<IMarkdownComponentProps>
                    dependencies={MarkdownComponentDependencies}
                    wrappedType={"ms.vss-markdown.markdown-component"}
                    wrappedProps={{
                        enableMentions: true,
                        onTextChanged: props.onChange,
                        isActive: !!onRenderPreview,
                        renderPreview: this._onRenderPreview,
                        autoAdjustHeight: true,
                        value: this.state.value,
                        autoFocus: true,
                        showToolbar: true,
                        className: "comment-markdown-component",
                        addAttachments: uploadImageHandler ? this._onAddAttachments : undefined,
                        validAttachmentTypes: ValidAttachmentTypes,
                        ariaLabel: !!ariaLabel ? ariaLabel : Resources.DefaultCommentEditorAriaLabel,
                        ariaLabelledBy: ariaLabelledBy,
                        placeholder: placeholder
                    }}
                    wrappedRef={this._editorRef}
                />
            </>
        );
    };

    private _onAddAttachments = async (files: File[], selectionStart: number, selectionEnd: number): Promise<void> => {
        if (!files || files.length < 1) {
            return;
        }

        const file: File = files[0];
        const { uploadImageSizeLimitByte } = this.props;
        if (file.size > uploadImageSizeLimitByte!) {
            return; // safety check
        }

        const fileExtension = file.type ? FileTypeToExtension[file.type] : "png";
        if (!fileExtension) {
            return; // type is specified, but not one we support
        }

        const { uploadImageHandler } = this.props;
        if (!uploadImageHandler) {
            return;
        }

        const currentText = this.state.value;
        const firstPart = currentText.substring(0, selectionStart);
        const endPart = currentText.substring(selectionEnd);

        // IE11 will specify empty file name when pasting
        const fileName = file.name || `image.${fileExtension}`;
        const blob = new Blob([file], { type: file.type });
        const blobUrl = URL.createObjectURL(blob);
        const tempLink = `![${fileName}](${blobUrl})`;
        const value = `${firstPart}${tempLink} ${endPart}`; // space is intentional
        this.setState({ value }, () => this._onChange(value));

        const finalUrl = await uploadImageHandler(file);
        if (this._isDisposing) {
            URL.revokeObjectURL(blobUrl);
        } else {
            this._blobUrlToFinalUrlMap![blobUrl] = finalUrl;
        }
    };

    private _onRenderPreview = (markdown: string): Promise<JSX.Element> => {
        const { onRenderPreview } = this.props;
        return new Promise((resolve, reject) => {
            if (onRenderPreview) {
                const result = this._editor!.getValue(true); // get the GUID/final value from the editor
                if (result instanceof Promise) {
                    result.then((translatedMarkdown) => {
                        resolve(onRenderPreview(translatedMarkdown));
                    });
                } else {
                    resolve(onRenderPreview(result));
                }
            } else {
                resolve(<>{markdown}</>);
            }
        });
    };

    private _onChange = (newValue: string): void => {
        const { comment, onChange } = this.props;
        onChange && onChange(comment.id, newValue);

        this.setState({ isDirty: this._getDirtyState(newValue), value: newValue });
    };

    private _getDirtyState(value: string): boolean {
        const editor = this._editor;

        return this._originalValue !== value && !!value && value.trim().length > 0 && (!editor || !editor.isEmpty());
    }

    private _editorRef = (ref: IRenderedEditor | IMarkdownComponent | undefined | null): void => {
        const { onEditorMounted, comment } = this.props;

        this._editor = ref;
        if (ref) {
            const result = ref.getValue();
            if (result instanceof Promise) {
                result.then((value) => {
                    this._originalValue = this.props.originalText !== undefined ? this.props.originalText : value;
                    this.setState({ isDirty: this._getDirtyState(value) });

                    if (onEditorMounted) {
                        onEditorMounted(comment.id);
                    }
                });
                return;
            }

            this._originalValue = this.props.originalText !== undefined ? this.props.originalText : result;
            this.setState({ isDirty: this._getDirtyState(result) });

            if (onEditorMounted) {
                onEditorMounted(comment.id);
            }
        }
    };

    private _renderButtons = (): JSX.Element => {
        const { updateButtonText, infoMessage } = this.props;

        return (
            <div className="comment-message-area">
                <div className="custom-message">{infoMessage}</div>
                <div className="actions-area">
                    <Button
                        className={"editor-cancel-button"}
                        text={CommonResources.Cancel}
                        onClick={this._onCancelClick}
                        ariaLabel={CommonResources.Cancel}
                    />
                    <Button
                        text={updateButtonText || Resources.Update}
                        ariaLabel={updateButtonText || Resources.Update}
                        disabled={!this.state.isDirty}
                        onClick={this._onUpdateClick}
                        primary
                    />
                </div>
            </div>
        );
    };

    private _renderDiscardDraftDialog = (): JSX.Element => {
        const cancelNavigateAwayClassName = "cancel-discard-draft";
        return (
            <Observer isDialogOpen={this._showDiscardDialog}>
                {(props: { isDialogOpen: boolean }) =>
                    props.isDialogOpen && (
                        <Dialog
                            titleProps={{ text: Resources.ConfirmDiscardCommentDraft }}
                            onDismiss={this._dismissDiscardDraftDialog}
                            lightDismiss={false}
                            defaultActiveElement={`.${cancelNavigateAwayClassName}`}
                            footerButtonProps={[
                                {
                                    text: CommonResources.Cancel,
                                    onClick: this._dismissDiscardDraftDialog,
                                    className: cancelNavigateAwayClassName
                                },
                                { text: Resources.Discard, danger: true, onClick: this._onConfirmDiscardClick }
                            ]}
                        />
                    )
                }
            </Observer>
        );
    };

    // dismisses the dialog if user presses cancel on the dialog
    private _dismissDiscardDraftDialog = (): void => {
        const { comment, onKeepDraft } = this.props;
        this._showDiscardDialog.value = false;

        if (!onKeepDraft) {
            return;
        }

        const result = this._editor!.getValue();
        if (result instanceof Promise) {
            result.then((value) => onKeepDraft(comment.id, value));
            return;
        }

        onKeepDraft(comment.id, result);
    };

    // discards the comment if users presses discard on the dialog
    private _onConfirmDiscardClick = (): void => {
        const { comment, onCancelEdit } = this.props;
        const { isDirty } = this.state;
        this.setState({ isDirty: false });

        onCancelEdit && onCancelEdit(comment.id, isDirty);
    };

    private _onUpdateClick = (): void => {
        const { isDirty } = this.state;

        if (!isDirty) {
            return;
        }

        const { comment, onUpdate } = this.props;
        const { text } = comment;

        this.setState({ isDirty: false });

        const result = this._editor!.getValue(true);
        if (result instanceof Promise) {
            result.then((value) => onUpdate(comment.id, this._resolveBlobUrl(value), text));
            return;
        }

        onUpdate(comment.id, this._resolveBlobUrl(result), text);
    };

    private _resolveBlobUrl(value: string): string {
        if (this.props.onRenderEditor) {
            return value;
        }

        // only resolve blob URLs for the default editor
        for (const blobUrl in this._blobUrlToFinalUrlMap) {
            value = value.replace(new RegExp(blobUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), this._blobUrlToFinalUrlMap[blobUrl]);
        }

        // also remove incomplete references still in the blob URL format
        return value.replace(/!\[[^\]]+\]\(blob:[^)]+\)/gi, "");
    }

    // shows the dialog if comment is dirty, if not just cancels
    private _onCancelClick = (): void => {
        const { isDirty } = this.state;

        if (isDirty) {
            this._showDiscardDialog.value = true;
        } else {
            const { comment, onCancelEdit } = this.props;

            onCancelEdit && onCancelEdit(comment.id, isDirty);
        }
    };
}

VssComponent.register("comment-editor", CommentEditor);
